import { useState, useEffect } from 'react';
import {
  getAllRestaurants,
  suspendRestaurant,
  activateRestaurant,
  deleteRestaurant
} from '../services/adminApi';

export const useAdminRestaurants = () => {
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const restaurantsPerPage = 20;

  const fetchRestaurants = async (page = 1, search = '', status = 'all') => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page,
        limit: restaurantsPerPage,
        search: search || undefined,
        status: status !== 'all' ? status : undefined
      };

      const response = await getAllRestaurants(params);

      // Gérer différents formats de réponse possibles
      if (response.results) {
        setRestaurants(response.results);
        setTotalPages(Math.ceil(response.count / restaurantsPerPage));
      } else if (Array.isArray(response)) {
        // Transformer les données pour inclure un statut basé sur is_active
        const transformed = response.map(r => ({
          ...r,
          status: r.is_active ? 'active' : 'suspended',
          owner: r.owner?.email || 'N/A',
          cuisine: r.cuisine_type || 'Non spécifié',
          address: r.address || 'Non spécifié',
          rating: r.rating || 0,
          reviews: r.reviews?.length || 0,
          orders: r.orders_count ?? 0,
          revenue: Number(r.total_revenue ?? 0)
        }));
        setRestaurants(transformed);
        setTotalPages(Math.ceil(transformed.length / restaurantsPerPage));
      } else {
        setRestaurants([]);
        setTotalPages(1);
      }

    } catch (err) {
      console.error('Erreur chargement restaurants:', err);

      if (err.message?.includes('401') || err.message?.includes('Unauthorized')) {
        setError('Session expirée - veuillez vous reconnecter');
      } else {
        setError(err.message || 'Erreur lors du chargement des restaurants');
      }

      setRestaurants([]);
    } finally {
      setLoading(false);
    }
  };
  const suspendRestaurantById = async (restaurantId, options = {}) => {
    try {
      await suspendRestaurant(restaurantId, options);
      // Recharger la liste
      await fetchRestaurants(currentPage, searchTerm, filterStatus);
      return { success: true };
    } catch (err) {
      console.error('Erreur suspension restaurant:', err);
      return { success: false, error: err.message };
    }
  };

  const activateRestaurantById = async (restaurantId) => {
    try {
      await activateRestaurant(restaurantId);
      // Recharger la liste
      await fetchRestaurants(currentPage, searchTerm, filterStatus);
      return { success: true };
    } catch (err) {
      console.error('Erreur activation restaurant:', err);
      return { success: false, error: err.message };
    }
  };

  const deleteRestaurantById = async (restaurantId) => {
    try {
      await deleteRestaurant(restaurantId);
      // Recharger la liste
      await fetchRestaurants(currentPage, searchTerm, filterStatus);
      return { success: true };
    } catch (err) {
      console.error('Erreur suppression restaurant:', err);
      return { success: false, error: err.message };
    }
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
    fetchRestaurants(1, term, filterStatus);
  };

  const handleFilterChange = (status) => {
    setFilterStatus(status);
    setCurrentPage(1);
    fetchRestaurants(1, searchTerm, status);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchRestaurants(page, searchTerm, filterStatus);
  };

  useEffect(() => {
    fetchRestaurants();
  }, []);

  return {
    restaurants,
    loading,
    error,
    searchTerm,
    filterStatus,
    currentPage,
    totalPages,
    suspendRestaurantById,
    activateRestaurantById,
    deleteRestaurantById,
    handleSearch,
    handleFilterChange,
    handlePageChange,
    refetch: () => fetchRestaurants(currentPage, searchTerm, filterStatus)
  };
};